// src/pages/InstructorSchedulePage.js
//
// Scheduling pilot (Pass 42) — the instructor's live personal schedule.
// "Tomorrow" is the default, fastest, clearest view (per the pilot spec). Powered
// by the real-time useInstructorSchedule hook, so an ops change appears here
// within seconds without any reload.

import React, { useState, useEffect, useMemo } from 'react';
import { PRODUCT_NAME } from '../config/product';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { getBookingInternal } from '../utils/booking';
import { useInstructorSchedule, isRecentlyChanged, dateKey } from '../hooks/useInstructorSchedule';
import { useInstructorLessons } from '../hooks/useInstructorLessons';
import { SCHEDULE_ENTRY_TYPES, SKILL_LABELS, isClientBooking, formatAdditionalGuests } from '../utils/scheduleModel';
import { bookingLessonEligibility, getOrCreateLessonInstanceForBooking, isDeliveryBearing } from '../utils/lessonHandoff';
import { detectOperationalConflicts } from '../utils/operationsWorkspace';
import { CURRENT_SEASON_ID } from '../utils/instructorCategory';
// Pass 83B §8 — the instructor day/time follows the SELECTED ORGANIZATION's operating timezone (generic
// zoned* helpers), mirroring Pass 82C.2 operations. §10 — the effective schedule-visibility policy is wired
// into the live hook. §15 — structured operating-area / service-location / meeting-point rendering.
// Pass 83B.1 — visible clock times are also org-zoned; org runtime resolution is shared via useOrgRuntime.
import { zonedDateKey, zonedShiftDays, wallClockToInstant, zonedTimeLabel, zonedDateLabel, LEGACY_OPS_TIMEZONE } from '../utils/opsTime';
import { effectiveInstructorScheduleVisibility } from '../utils/scheduleVisibility';
import { useSelectedOrganizationContext } from '../utils/organizationSelection';
import { useOrgRuntime } from '../hooks/useOrgRuntime';
import { useOrgAreaConfig } from '../hooks/useOrgAreaConfig';
import { areaKeyOf } from '../utils/operatingAreas';
import LessonCloseoutModal from '../components/instructor/LessonCloseoutModal';

const C = {
  bg: '#FAFAF8', card: '#FFFFFF', card2: '#F5F4F0', border: 'rgba(0,0,0,0.07)',
  blue: '#2563EB', blueDim: 'rgba(37,99,235,0.08)', green: '#16A34A', greenDim: 'rgba(22,163,74,0.09)',
  amber: '#D97706', amberDim: 'rgba(217,119,6,0.10)', red: '#DC2626', redDim: 'rgba(220,38,38,0.08)',
  t1: '#1C1B18', t2: '#57564F', t3: '#8A8980',
};
const font = "'DM Sans',system-ui,sans-serif";
const mono = "'DM Mono',ui-monospace,monospace";

function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return v instanceof Date ? v : new Date(v);
}
// Pass 83B.1 §2/§3 — visible operational times are formatted in the SELECTED ORG's IANA zone (generic
// zonedTimeLabel), NEVER the browser's local zone. The booking day was already grouped in the org zone
// (83B); this makes the CLOCK on the card match. A Denver 09:00 lesson reads 09:00 to a viewer in Japan.
const fmtTime = (v, tz) => { const d = toDate(v); return d ? zonedTimeLabel(d, tz) : '—'; };
const fmtRange = (a, b, tz) => `${fmtTime(a, tz)}${b ? ` – ${fmtTime(b, tz)}` : ''}`;
// §4 — a pure YYYY-MM-DD day key → human label WITHOUT going through the browser zone. Build a fixed UTC
// noon instant from the key parts and format in UTC, so the weekday/month/day never shift with the viewer.
const fmtDayLabel = (key) => {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  return date.toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric' });
};
const fmtAgo = (d, tz) => {
  if (!d) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return zonedTimeLabel(d, tz);
};

const STATUS_TONE = {
  canceled: { bg: C.redDim, fg: C.red, label: 'Cancelled' },
  completed: { bg: C.greenDim, fg: C.green, label: 'Completed' },
  no_show: { bg: C.amberDim, fg: C.amber, label: 'No-show' },
  in_progress: { bg: C.blueDim, fg: C.blue, label: 'In progress' },
};

// Pass 83B §15/§16/§17/§18 — resolve the structured operating-area / service-location / meeting-point for a
// booking into human-readable labels using the org's area config, with a graceful fallback to the legacy
// `locationLabel` / accommodation* fields for old (source-backed) bookings. Never renders raw ids. Source
// pickup text stays source-owned. `showArea` keeps single-area orgs uncluttered.
const titleCaseKey = (k) => String(k || '').split(/[-_]/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
function resolveStructuredLocation(b, { locById, mpById, showArea }) {
  const svc = b.serviceLocationId ? locById.get(b.serviceLocationId) : null;
  const mp = b.meetingPointId ? mpById.get(b.meetingPointId) : null;
  const areaKey = areaKeyOf(b.operatingAreaId || b.serviceLocationId || b.meetingPointId || '');
  const serviceName = svc?.name || '';
  const areaLabel = titleCaseKey(areaKey);
  return {
    // area shown only when the org runs more than one area (or the booking is outside the ordinary area);
    // for single-area orgs this stays blank to avoid clutter (§17).
    areaLabel: showArea && areaLabel ? areaLabel : '',
    serviceName,
    transportRequired: !!svc?.transportRequired,
    // meeting/pickup: structured point name → legacy locationLabel fallback (§16).
    meetingLabel: mp?.name || b.locationLabel || '',
    meetingAddress: mp?.address || '',
    mapUrl: mp?.mapUrl || b.accommodationMapUrl || '',
  };
}

function LessonDeliveryAction({ b, lesson, onStartLesson, onCloseoutLesson }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // §43 a prior-season booking is historical → read-only (view only, no new lesson).
  const historical = !!(b.seasonId && b.seasonId !== CURRENT_SEASON_ID);
  const elig = bookingLessonEligibility(b, { readOnly: historical });
  if (!onStartLesson) return null;
  const hasLesson = !!lesson;
  const completed = hasLesson && (String(lesson.status) === 'completed' || isDeliveryBearing(lesson));
  // completed lessons can always be viewed; otherwise the booking must be eligible to start/open.
  if (!completed && !elig.eligible) return null;
  const canDeliver = elig.eligible; // current, assigned, not historical → a closeout may write delivery evidence
  const builderLabel = completed ? 'View lesson' : (hasLesson ? 'Open lesson' : 'Open builder');
  const go = async () => { if (busy) return; setBusy(true); setError(''); try { await onStartLesson(b); } catch (e) { setError(e?.message || 'Could not open the lesson.'); } finally { setBusy(false); } };
  return (
    <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Pass 84 — the ONE-MINUTE closeout is the primary fast path from the schedule; the full Lesson Builder
          stays available as the secondary action. A historical/ineligible booking shows only "View lesson". */}
      {canDeliver && onCloseoutLesson && (
        <button type="button" data-testid={`closeout-lesson-${b.id}`} onClick={() => onCloseoutLesson(b)}
          style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: completed ? C.green : C.blue, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
          {completed ? 'Edit closeout' : 'Close out lesson'}
        </button>
      )}
      <button type="button" data-testid={`start-lesson-${b.id}`} disabled={busy} onClick={go}
        style={{ padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.card, color: C.t2, cursor: busy ? 'default' : 'pointer', fontSize: 13.5, fontWeight: 700 }}>{busy ? 'Opening…' : builderLabel}</button>
      {error ? <div role="alert" style={{ color: C.red, fontSize: 12.5, marginTop: 6, width: '100%' }}>{error}</div> : null}
    </div>
  );
}

function LessonCard({ b, highlight, internalNote, lesson, onStartLesson, onCloseoutLesson, conflict, nowState, loc = null, tz = LEGACY_OPS_TIMEZONE }) {
  const changed = isRecentlyChanged(b);
  const cancelled = b.status === 'canceled';
  const tone = STATUS_TONE[b.status];
  const internal = !isClientBooking(b);
  const entry = SCHEDULE_ENTRY_TYPES[b.scheduleEntryType] || SCHEDULE_ENTRY_TYPES.client_booking;
  const extraGuests = formatAdditionalGuests(b.additionalGuests || []);
  const L = loc || {};
  // Pass 58A: an RDO / staff time-off entry is all-day (no startAt) and must read as "Day Off (RDO)",
  // never as a lesson — no guest, price, payment, or coaching affordance (none are rendered here anyway).
  const isTimeoff = b.activityType === 'timeoff' || b.scheduleEntryType === 'blocked';
  const allDay = b.allDay === true || b.hasOperationalTime === false;
  return (
    <div style={{
      background: C.card,
      border: `1px solid ${highlight ? C.blue : changed ? 'rgba(217,119,6,0.35)' : C.border}`,
      borderLeft: `4px solid ${cancelled ? C.red : changed ? C.amber : internal ? entry.color : C.blue}`,
      borderRadius: 14, padding: '16px 18px', marginBottom: 12,
      opacity: cancelled ? 0.72 : 1,
      boxShadow: highlight ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div data-testid="entry-time" style={{ fontFamily: mono, fontSize: 20, fontWeight: 700, color: cancelled ? C.t3 : C.t1, textDecoration: cancelled ? 'line-through' : 'none' }}>
          {allDay ? 'All day' : fmtRange(b.startAt, b.endAt, tz)}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1 }}>
          {isTimeoff ? 'Day Off' : (b.bookingType || b.serviceNameSnapshot || b.serviceType || 'Lesson')}
        </div>
        {isTimeoff
          ? <span data-testid="rdo-badge" style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: entry.color, padding: '3px 10px', borderRadius: 20 }}>RDO · DAY OFF</span>
          : internal && <span style={{ fontSize: 11, fontWeight: 800, color: '#fff', background: entry.color, padding: '3px 10px', borderRadius: 20 }}>{entry.label.toUpperCase()}</span>}
        {b.discipline && !internal && <span style={{ fontSize: 12, color: C.t2, background: C.card2, padding: '2px 9px', borderRadius: 20 }}>{b.discipline}</span>}
        {b.skillLevel && SKILL_LABELS[b.skillLevel] && <span style={{ fontSize: 12, color: C.t2, background: C.card2, padding: '2px 9px', borderRadius: 20 }}>{SKILL_LABELS[b.skillLevel]}</span>}
        {L.areaLabel && <span data-testid="area-chip" style={{ fontSize: 12, color: C.t2, background: C.card2, padding: '2px 9px', borderRadius: 20 }}>{L.areaLabel}</span>}
        {L.transportRequired && !cancelled && <span data-testid="transport-required" style={{ fontSize: 11, fontWeight: 700, color: C.amber, background: C.amberDim, padding: '2px 9px', borderRadius: 20 }}>🚐 Transport required</span>}
        <div style={{ flex: 1 }} />
        {nowState === 'now' && !cancelled && (
          <span data-testid="now-badge" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: '#fff', background: C.green, padding: '3px 10px', borderRadius: 20 }}>NOW</span>
        )}
        {nowState === 'next' && !cancelled && (
          <span data-testid="next-badge" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: C.blue, background: C.blueDim, padding: '3px 10px', borderRadius: 20 }}>NEXT</span>
        )}
        {conflict && !cancelled && (
          <span data-testid="conflict-badge" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: C.red, background: C.redDim, padding: '3px 10px', borderRadius: 20 }}>⚠ CONFLICT</span>
        )}
        {changed && !cancelled && (
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: C.amber, background: C.amberDim, padding: '3px 10px', borderRadius: 20 }}>
            ● CHANGED
          </span>
        )}
        {tone && (
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: tone.fg, background: tone.bg, padding: '3px 10px', borderRadius: 20 }}>
            {tone.label.toUpperCase()}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '8px 20px', marginTop: 12 }}>
        {!internal && <Field label="Guest / group" value={b.requesterName || '—'} />}
        {b.groupSize != null && <Field label="Group size" value={String(b.groupSize)} />}
        {extraGuests && <Field label="Additional guests" value={extraGuests} />}
        {/* §15/§16 — structured service location (named) with a graceful fallback to the legacy meeting label. */}
        {L.serviceName && <Field label="Service location" value={L.serviceName} />}
        <Field label="Meeting / pickup" value={L.meetingLabel || b.locationLabel || '—'} />
        {L.meetingAddress && <Field label="Meeting address" value={L.meetingAddress} />}
        {b.language && <Field label="Language" value={b.language} />}
        {b.transportInfo && <Field label="Transport / pickup" value={b.transportInfo} />}
        {b.accommodationName && <Field label="Accommodation" value={b.accommodationName} />}
      </div>

      {(L.mapUrl || b.accommodationMapUrl) && (
        <a href={L.mapUrl || b.accommodationMapUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13, fontWeight: 700, color: '#fff', background: C.blue, padding: '7px 14px', borderRadius: 9, textDecoration: 'none' }}>
          📍 Open meeting / accommodation map
        </a>
      )}

      {b.notes && (
        <div style={{ marginTop: 10, fontSize: 13, color: C.t2, background: C.card2, borderRadius: 10, padding: '9px 12px', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: C.t1 }}>Notes: </span>{b.notes}
        </div>
      )}
      {internalNote && (
        <div style={{ marginTop: 8, fontSize: 12, color: C.amber, background: C.amberDim, borderRadius: 10, padding: '8px 12px', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700 }}>Ops: </span>{internalNote}
        </div>
      )}

      {/* Pass 67 — lesson delivery handoff (Pass 66). Only for an eligible assigned client lesson; never for
          RDO/driving/admin/cancelled/historical. Start → deterministic lessonInstance → Lesson Builder. */}
      {!cancelled && !internal && !isTimeoff && <LessonDeliveryAction b={b} lesson={lesson} onStartLesson={onStartLesson} onCloseoutLesson={onCloseoutLesson} />}

      <div style={{ marginTop: 10, fontSize: 11, color: C.t3, fontFamily: mono }}>
        {/* §3 — absolute "Last updated" in the org zone, not the browser's. */}
        Last updated {toDate(b.updatedAt) ? `${zonedDateLabel(toDate(b.updatedAt), tz)} ${zonedTimeLabel(toDate(b.updatedAt), tz)}` : '—'}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.t3 }}>{label}</div>
      <div style={{ fontSize: 14, color: C.t1, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function DaySection({ title, subtitle, lessons, highlightId, emptyText, internalMap = {}, lessonsByBooking = null, onStartLesson = null, onCloseoutLesson = null, conflictSet = null, nowStateOf = null, footer = null, locOf = null, tz = LEGACY_OPS_TIMEZONE }) {
  const active = lessons.filter((l) => l.status !== 'canceled');
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: C.t1, margin: 0 }}>{title}</h2>
        {subtitle && <span style={{ fontSize: 13, color: C.t3 }}>{subtitle}</span>}
        <span style={{ fontSize: 12, color: C.t2, background: C.card2, padding: '2px 10px', borderRadius: 20 }}>
          {active.length} {active.length === 1 ? 'activity' : 'activities'}
        </span>
      </div>
      {lessons.length === 0
        ? (<div style={{ fontSize: 14, color: C.t3, background: C.card, border: `1px dashed ${C.border}`, borderRadius: 12, padding: '20px', textAlign: 'center' }}>{emptyText}{footer}</div>)
        : lessons.map((l) => (
          <LessonCard key={l.id} b={l} highlight={l.id === highlightId} internalNote={internalMap[l.id]}
            lesson={lessonsByBooking ? lessonsByBooking.get(l.id) : null}
            onStartLesson={onStartLesson}
            onCloseoutLesson={onCloseoutLesson}
            conflict={conflictSet ? conflictSet.has(l.id) : false}
            nowState={nowStateOf ? nowStateOf(l) : null}
            loc={locOf ? locOf(l) : null} tz={tz} />
        ))}
    </div>
  );
}

export default function InstructorSchedulePage({ organizationId: orgIdProp = '' } = {}) {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { selectedOrganization } = useSelectedOrganizationContext(userData);
  const [params] = useSearchParams();
  const focusDate = params.get('date') || '';
  const focusBooking = params.get('booking') || '';

  // Pass 83B §4/§8/§10 — resolve the SELECTED ORGANIZATION and its canonical member. The org is passed by
  // the workspace shell; standalone it resolves the same way as the shell. Everything downstream (schedule
  // query, timezone, visibility policy, area config) is scoped to this one org so a dual-org human never
  // sees a blended schedule.
  const orgId = orgIdProp || selectedOrganization?.organizationId || userData?.orgId || '';
  const member = useMemo(
    () => (userData?.organizationMemberships || []).find((m) => m.organizationId === orgId) || null,
    [userData, orgId],
  );

  // §8/§16 — resolve the org operating runtime (timezone / season / visibility source) via the SHARED hook,
  // so My Schedule and My Calendar use identical context and a failed org-doc fetch never silently becomes
  // UTC. SAS resolves to Asia/Tokyo (stays visually identical); a non-JST org sees its own day.
  const { orgDoc, orgLoadFailed, orgTz, tz, orgRuntimeError, seasonId } = useOrgRuntime(orgId, selectedOrganization);
  const visibilityPolicy = useMemo(
    () => (orgDoc && !orgLoadFailed && member ? effectiveInstructorScheduleVisibility({ organization: orgDoc, member, seasonId }) : null),
    [orgDoc, orgLoadFailed, member, seasonId],
  );

  const { loading, error, lastUpdatedAt, scopeMissing, tomorrowKey, week, byDay, all, publishedThrough, visibilityApplicable } = useInstructorSchedule(
    currentUser?.uid,
    { organizationId: orgId, timeZone: orgTz, visibilityPolicy },
  );
  // Pass 84.2 — scope the live lesson query to the CURRENT canonical business member id (relink-safe), never the stored
  // Auth snapshot. `member` is the caller's canonical membership for this org (resolved above); missing → fail closed.
  const lessonsByBooking = useInstructorLessons(currentUser?.uid, orgId, member?.userId || '');

  // §15 — structured area config for this org; build id→record maps for name/transport/address lookup.
  const { areas, serviceLocations, meetingPoints } = useOrgAreaConfig(orgId, orgDoc);
  const locById = useMemo(() => new Map((serviceLocations || []).map((l) => [l.id, l])), [serviceLocations]);
  const mpById = useMemo(() => new Map((meetingPoints || []).map((m) => [m.id, m])), [meetingPoints]);
  const showArea = (areas || []).length > 1; // §17 — only surface the area when the org runs more than one.
  const locOf = useMemo(() => (b) => resolveStructuredLocation(b, { locById, mpById, showArea }), [locById, mpById, showArea]);

  // Pass 57C (item 11): honest publication copy. A restricted School horizon shows "Published through
  // <date>"; otherwise the schedule is fully published. (Never reveals hidden future counts.)
  const publicationCopy = visibilityApplicable
    ? (publishedThrough ? `Published through ${publishedThrough}` : 'Self-managed calendar')
    : 'Full season published';

  // Pass 67 — DAY-FIRST surface. §8 — the visible day is an ORG-ZONE day key; default = TODAY. Prev/Today/Next step it.
  const todayKey = zonedDateKey(new Date(), tz);
  const [viewKey, setViewKey] = useState(focusDate || todayKey);
  const [showWeek, setShowWeek] = useState(false);
  const shiftDay = (n) => setViewKey(zonedDateKey(zonedShiftDays(wallClockToInstant(viewKey, '12:00', tz), n, tz), tz));
  useEffect(() => { if (focusDate) setViewKey(focusDate); }, [focusDate]);
  // §7 — on an org switch, reset the visible day to the (new org's) Today so a stale day/timezone never
  // carries across tenants. Skipped when the URL pins a focus date.
  useEffect(() => {
    if (focusDate) return;
    setViewKey(zonedDateKey(new Date(), orgTz || LEGACY_OPS_TIMEZONE));
    setShowWeek(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // §24 conflicts — reuse the canonical operational overlap engine (no second engine).
  const conflictSet = useMemo(() => detectOperationalConflicts(all || []), [all]);

  // §26/§27 now-state within the visible day (presentation only; derived from JST now vs start/end).
  const nowMs = Date.now();
  const dayList = useMemo(() => (byDay[viewKey] || []), [byDay, viewKey]);
  const nowStateOf = useMemo(() => {
    const active = dayList.filter((b) => b.status !== 'canceled' && b.startAt);
    const upcoming = active.filter((b) => (toDate(b.startAt)?.getTime() || 0) > nowMs).sort((a, b) => (toDate(a.startAt)?.getTime() || 0) - (toDate(b.startAt)?.getTime() || 0));
    const nextId = viewKey === todayKey && upcoming[0] ? upcoming[0].id : '';
    return (b) => {
      if (!b.startAt || viewKey !== todayKey) return null;
      const s = toDate(b.startAt)?.getTime() || 0; const e = toDate(b.endAt)?.getTime() || (s + 3600000);
      if (s <= nowMs && nowMs < e) return 'now';
      if (b.id === nextId) return 'next';
      return null;
    };
  }, [dayList, nowMs, viewKey, todayKey]);

  // §11 next scheduled day (active work) strictly after the visible day.
  const nextScheduledKey = useMemo(() => {
    const keys = Object.keys(byDay).filter((k) => k > viewKey && (byDay[k] || []).some((b) => b.status !== 'canceled')).sort();
    return keys[0] || '';
  }, [byDay, viewKey]);

  // §17/§54 — Start/Open lesson (Pass 66). Resolve the actor's canonical member id for the booking's org;
  // authorization is the assigned instructor (Auth uid) + fail-closed. Then open the existing Lesson Builder.
  const startLesson = async (b) => {
    const org = b.organizationId;
    const member = (userData?.organizationMemberships || []).find((m) => m.organizationId === org);
    const { lessonId } = await getOrCreateLessonInstanceForBooking(db, b, {
      userData, organizationId: org, actorInstructorId: member?.userId || '', actorAuthUserId: currentUser?.uid || '',
      readOnly: !!(b.seasonId && b.seasonId !== CURRENT_SEASON_ID),
    });
    navigate(`/instructor/lesson-builder?lessonId=${encodeURIComponent(lessonId)}`);
  };

  // Pass 84 — the one-minute closeout. Open the fast modal for a booking (never navigates away); the modal reuses
  // the canonical lessonInstance writers. Resolve the actor's canonical member id + org context here (same
  // fail-closed identity resolution as startLesson) so the modal never trusts a client-supplied identity.
  const [closeout, setCloseout] = useState(null); // { booking, ctx } | null
  const openCloseout = (b) => {
    const org = b.organizationId;
    const m = (userData?.organizationMemberships || []).find((mm) => mm.organizationId === org);
    setCloseout({
      booking: b,
      ctx: {
        userData, organizationId: org, actorInstructorId: m?.userId || '', actorAuthUserId: currentUser?.uid || '',
        readOnly: !!(b.seasonId && b.seasonId !== CURRENT_SEASON_ID),
      },
    });
  };

  const [internalMap, setInternalMap] = useState({});

  // Fetch staff-only ops notes for visible lessons (rules allow the assigned
  // instructor to read their own lesson's bookingInternal doc).
  const idsKey = (all || []).map((b) => b.id).join(',');
  useEffect(() => {
    let cancelled = false;
    const ids = idsKey ? idsKey.split(',') : [];
    if (!ids.length) { setInternalMap({}); return undefined; }
    (async () => {
      const entries = await Promise.all(ids.map(async (id) => {
        try { const doc = await getBookingInternal(db, id); return [id, doc?.internalNotes || '']; }
        catch { return [id, '']; }
      }));
      if (!cancelled) setInternalMap(Object.fromEntries(entries.filter(([, v]) => v)));
    })();
    return () => { cancelled = true; };
  }, [idsKey]);
  // Re-render the "updated Xs ago" label periodically.
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 15000); return () => clearInterval(t); }, []);

  // §14/§16/§52 — honest, mutually-exclusive setup states. noOrg: no selected organization could be
  // resolved (the query never ran; fail-closed). orgRuntimeError: the org doc fetch failed and no timezone
  // is known (don't invent one). notLinked: logged in but not a member of this org. Each is distinct from a
  // genuine empty day.
  const noOrg = !orgId || scopeMissing;
  const notLinked = !!(orgId && (orgDoc || orgLoadFailed) && !member);
  const blockedState = noOrg || orgRuntimeError || notLinked;
  const dayTitle = viewKey === todayKey ? 'Today' : viewKey === tomorrowKey ? 'Tomorrow' : fmtDayLabel(viewKey);
  const stepBtn = (label, onClick, testid, active = false) => (
    <button type="button" data-testid={testid} onClick={onClick} style={{
      padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: font,
      border: `1px solid ${active ? C.blue : C.border}`, background: active ? C.blue : C.card, color: active ? '#fff' : C.t2,
    }}>{label}</button>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.t1, fontFamily: font }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 22px 80px' }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: C.t2, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 14 }}>← Dashboard</button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
          <h1 style={{ fontSize: 30, fontWeight: 900, margin: 0, letterSpacing: -0.5 }}>My Schedule</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.t2 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: error ? C.red : C.green, boxShadow: `0 0 8px ${error ? C.red : C.green}`, display: 'inline-block' }} />
            {/* §20 — this reflects the Firestore listener heartbeat, NOT upstream source freshness; the copy
                says "<Product> live" so it never falsely implies the SAS spreadsheet source was just re-synced. */}
            {error ? 'Reconnecting…' : `${PRODUCT_NAME} live · updated ${fmtAgo(lastUpdatedAt, tz)}`}
          </div>
        </div>
        {/* Pass 57C (item 11): publication window — never reveals hidden future counts. */}
        <div data-testid="publication-copy" style={{ fontSize: 12, color: C.t3, marginBottom: 6 }}>{publicationCopy}</div>

        {/* Day-first navigation: Prev · Today · Next + a compact This-week toggle. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 24px', flexWrap: 'wrap' }}>
          {stepBtn('‹ Prev', () => { setShowWeek(false); shiftDay(-1); }, 'day-prev')}
          {stepBtn('Today', () => { setShowWeek(false); setViewKey(todayKey); }, 'day-today', !showWeek && viewKey === todayKey)}
          {stepBtn('Next ›', () => { setShowWeek(false); shiftDay(1); }, 'day-next')}
          <input type="date" data-testid="day-picker" value={viewKey} onChange={(e) => { if (e.target.value) { setShowWeek(false); setViewKey(e.target.value); } }}
            style={{ padding: '7px 10px', borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: font }} />
          <div style={{ flex: 1 }} />
          {stepBtn('This week', () => setShowWeek((v) => !v), 'view-week', showWeek)}
        </div>

        {/* §14 — no selected organization could be resolved: the query never ran (fail-closed), so this is
            NOT a "No work scheduled" empty day. */}
        {noOrg && (
          <div data-testid="no-org" style={{ fontSize: 14, color: C.amber, background: C.amberDim, border: `1px solid ${C.amber}`, borderRadius: 12, padding: '16px', textAlign: 'center' }}>
            Your organization context could not be loaded. Reopen the workspace or reselect your organization.
          </div>
        )}
        {/* §16 — the org doc failed to load and no timezone is known: surface the runtime failure rather than
            render an invented (UTC) zone. */}
        {!noOrg && orgRuntimeError && (
          <div data-testid="org-runtime-error" style={{ fontSize: 14, color: C.red, background: C.redDim, border: `1px solid ${C.red}`, borderRadius: 12, padding: '16px', textAlign: 'center' }}>
            Could not load your organization’s settings. Please retry — your schedule times depend on it.
          </div>
        )}
        {/* §52 — a logged-in user with no canonical membership in the selected org must see a clear access/
            setup problem, never a silent "No work scheduled". Distinct from loading / error / empty. */}
        {!noOrg && !orgRuntimeError && notLinked && (
          <div data-testid="not-linked" style={{ fontSize: 14, color: C.amber, background: C.amberDim, border: `1px solid ${C.amber}`, borderRadius: 12, padding: '16px', textAlign: 'center' }}>
            Your account isn’t linked to this organization’s team yet. Ask an owner or admin to prepare your invitation, then accept it to see your schedule.
          </div>
        )}
        {!blockedState && loading && <div data-testid="schedule-loading" style={{ color: C.t3, fontSize: 14 }}>Loading your schedule…</div>}
        {!blockedState && !loading && error && <div data-testid="schedule-error" style={{ fontSize: 14, color: C.red, background: C.redDim, border: `1px solid ${C.red}`, borderRadius: 12, padding: '16px', textAlign: 'center' }}>Could not load your schedule. Check your connection and try again.</div>}

        {!blockedState && !loading && !error && !showWeek && (
          <DaySection title={dayTitle} subtitle={fmtDayLabel(viewKey)} lessons={dayList} highlightId={focusBooking}
            internalMap={internalMap} lessonsByBooking={lessonsByBooking} onStartLesson={startLesson} onCloseoutLesson={openCloseout}
            conflictSet={conflictSet} nowStateOf={nowStateOf} locOf={locOf} tz={tz}
            emptyText={`No work scheduled ${viewKey === todayKey ? 'today' : 'this day'}.`}
            footer={nextScheduledKey ? (
              <div style={{ marginTop: 12 }}>
                <button type="button" data-testid="next-scheduled" onClick={() => setViewKey(nextScheduledKey)}
                  style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${C.blue}`, background: C.card, color: C.blue, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                  Next scheduled day → {fmtDayLabel(nextScheduledKey)}
                </button>
              </div>
            ) : null} />
        )}

        {!blockedState && !loading && !error && showWeek && (
          Object.keys(byDay).filter((k) => week.some((w) => (w.startAt ? dateKey(w.startAt, tz) : w.serviceDate) === k)).sort().map((k) => (
            <DaySection key={k} title={fmtDayLabel(k)} lessons={byDay[k]} highlightId={focusBooking} internalMap={internalMap}
              lessonsByBooking={lessonsByBooking} onStartLesson={startLesson} onCloseoutLesson={openCloseout} conflictSet={conflictSet} nowStateOf={nowStateOf} locOf={locOf} tz={tz} emptyText="" />
          ))
        )}
        {!blockedState && !loading && !error && showWeek && week.length === 0 && (
          <div style={{ fontSize: 14, color: C.t3 }}>No work in the next 7 days.</div>
        )}
      </div>
      {closeout && (
        <LessonCloseoutModal
          booking={closeout.booking}
          lesson={lessonsByBooking ? lessonsByBooking.get(closeout.booking.id) : null}
          ctx={closeout.ctx}
          db={db}
          onClose={() => setCloseout(null)}
          onSaved={() => { /* real-time useInstructorLessons refresh reflects the delivered lesson */ }}
        />
      )}
    </div>
  );
}
